<template>
  <div class="pa-4">
    <breadcrumb />

    <div class="flex-wrap mb-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2 class="mr-3 black--text">{{ $t('reports.cropProgression.title') }}</h2>
        <v-btn small rounded color="primary" depressed @click="showGuide = true">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ $t('reports.cropProgression.aiReportGuide') }}
        </v-btn>
      </div>
      <div class="header-actions">
        <v-btn outlined color="primary" class="mr-2" @click="cancel">
          {{ $t('reports.cropProgression.cancel') }}
        </v-btn>
        <v-btn color="primary" depressed :disabled="!canGenerate" :loading="generating" @click="generate">
          {{ $t('reports.cropProgression.generateReport') }}
        </v-btn>
      </div>
    </div>

    <v-card class="pa-4" outlined>
      <v-row>
        <!-- Map -->
        <v-col cols="12" md="8">
          <h3 class="mb-1 black--text">{{ $t('reports.cropProgression.selectFarmGeofence') }}</h3>
          <p class="mb-3 grey--text">{{ $t('reports.cropProgression.selectFarmGeofenceHelp') }}</p>
          <geofence-map
            :source="source"
            :zone="selectedZone"
            :geojson-coordinates="geojsonCoords"
            :radius-meters="circleRadius"
            @update:geometry="onDrawGeometry"
          />
        </v-col>

        <!-- Right panel -->
        <v-col cols="12" md="4">
          <v-btn-toggle v-model="source" mandatory dense class="flex-wrap mb-4 d-flex">
            <v-btn value="farm_zone">{{ $t('reports.cropProgression.sourceFarmZone') }}</v-btn>
            <v-btn value="polygon">{{ $t('reports.cropProgression.sourcePolygon') }}</v-btn>
            <v-btn value="circle">{{ $t('reports.cropProgression.sourceCircle') }}</v-btn>
            <v-btn value="geojson">{{ $t('reports.cropProgression.sourceGeojson') }}</v-btn>
          </v-btn-toggle>

          <!-- Farm/Zone -->
          <div v-if="source === 'farm_zone'">
            <label class="mb-1 d-block black--text">{{ $t('reports.cropProgression.selectFarmZone') }}</label>
            <farm-zone-select v-model="selectedZone" />
          </div>

          <!-- Draw polygon / circle: instructions -->
          <div v-else-if="source === 'polygon'">
            <v-alert dense text type="info" class="mb-0">
              {{ $t('reports.cropProgression.drawPolygon') }} — {{ $t('reports.cropProgression.drawPolygonHelp') }}
            </v-alert>
          </div>
          <div v-else-if="source === 'circle'">
            <v-alert dense text type="info" class="mb-3">
              {{ $t('reports.cropProgression.drawCircle') }} — {{ $t('reports.cropProgression.drawCircleHelp') }}
            </v-alert>
            <v-text-field
              v-model.number="circleRadius"
              :label="$t('reports.cropProgression.radiusM')"
              type="number"
              min="1"
              suffix="m"
              outlined
              dense
              hide-details
            />
          </div>

          <!-- Upload GeoJSON -->
          <div v-else-if="source === 'geojson'">
            <h4 class="mb-1 primary--text">{{ $t('reports.cropProgression.uploadGeojsonFile') }}</h4>
            <p class="mb-2 grey--text text-caption">{{ $t('reports.cropProgression.uploadGeojsonHelp') }}</p>
            <v-file-input
              accept=".geojson,.json"
              :label="$t('reports.cropProgression.selectAFile')"
              prepend-icon="mdi-upload"
              outlined
              dense
              hide-details
              :loading="uploadingGeojson"
              @change="onGeojsonUpload"
            />
          </div>

          <v-divider class="my-4" />

          <!-- Available date -->
          <label class="mb-1 d-block black--text">{{ $t('reports.cropProgression.selectDate') }}</label>
          <div v-if="datesLoading" class="mb-2 grey--text">
            <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2" />
            {{ $t('reports.cropProgression.fetchingDates') }}
          </div>
          <v-select
            v-else
            v-model="selectedDate"
            :items="dateItems"
            :placeholder="$t('reports.cropProgression.select')"
            :no-data-text="
              hasGeofence
                ? $t('reports.cropProgression.noAvailableDates')
                : $t('reports.cropProgression.selectDateToGenerate')
            "
            outlined
            dense
            hide-details
          />
        </v-col>
      </v-row>
      <!-- Estimated compute cost -->
      <estimated-compute-cost report-type="vegetation_vigor" :geofence="geofencePayload" />
    </v-card>

    <guide-dialog :value="showGuide" @input="showGuide = $event" />
  </div>
</template>

<script>
import CropProgressionService from '@/_services/CropProgressionService';
import S3UploadService from '@/_services/S3UploadService';
import GeofenceMap from './components/GeofenceMap.vue';
import FarmZoneSelect from './components/FarmZoneSelect.vue';
import GuideDialog from './components/GuideDialog.vue';
import EstimatedComputeCost from '../shared/EstimatedComputeCost.vue';
import reportError from '@/mixins/reportError';

const DEFAULT_CIRCLE_RADIUS_M = 20; // meters

export default {
  name: 'CropProgressionCreate',
  components: { GeofenceMap, FarmZoneSelect, GuideDialog, EstimatedComputeCost },
  mixins: [reportError],
  data() {
    return {
      source: 'farm_zone',
      selectedZone: null,
      circleRadius: DEFAULT_CIRCLE_RADIUS_M, // meters; two-way synced with the map circle
      drawnGeometry: null,
      geojsonCoords: [],
      geojsonS3Path: null,
      uploadingGeojson: false,
      dates: [],
      datesLoading: false,
      selectedDate: null,
      generating: false,
      showGuide: false,
      datesTimer: null,
    };
  },
  computed: {
    geofencePayload() {
      switch (this.source) {
        case 'farm_zone':
          return this.selectedZone
            ? {
                geofenceType: 'farm_zone',
                geofenceId: this.selectedZone.geofenceId,
                farmId: this.selectedZone.farmId,
              }
            : null;
        case 'polygon':
          return this.drawnGeometry && this.drawnGeometry.shape === 'polygon'
            ? { geofenceType: 'polygon', coordinates: this.drawnGeometry.coordinates }
            : null;
        case 'circle':
          return this.drawnGeometry && this.drawnGeometry.shape === 'circle'
            ? {
                geofenceType: 'circle',
                latitude: this.drawnGeometry.latitude,
                longitude: this.drawnGeometry.longitude,
                radius: this.drawnGeometry.radius,
              }
            : null;
        case 'geojson':
          return this.geojsonCoords.length >= 3
            ? {
                geofenceType: 'geojson',
                coordinates: this.geojsonCoords,
                geojsonS3Path: this.geojsonS3Path,
              }
            : null;
        default:
          return null;
      }
    },
    hasGeofence() {
      return !!this.geofencePayload;
    },
    dateItems() {
      return (this.dates || []).map((d) => ({
        text: this.$options.filters.formatDate(d),
        value: d,
      }));
    },
    canGenerate() {
      return this.hasGeofence && !!this.selectedDate && !this.generating;
    },
  },
  watch: {
    source() {
      // Reset per-source state when switching source.
      this.drawnGeometry = null;
      if (this.source !== 'geojson') {
        this.geojsonCoords = [];
        this.geojsonS3Path = null;
      }
      if (this.source !== 'farm_zone') this.selectedZone = null;
      if (this.source === 'circle') this.circleRadius = DEFAULT_CIRCLE_RADIUS_M; // default radius
      this.resetDates();
    },
    geofencePayload() {
      this.scheduleFetchDates();
    },
  },
  methods: {
    onDrawGeometry(geom) {
      this.drawnGeometry = geom;
      // Map circle -> radius field (guard against feedback loop).
      if (geom && geom.shape === 'circle' && geom.radius != null) {
        const meters = Math.round(geom.radius * 1000);
        if (Math.abs(meters - Number(this.circleRadius)) >= 1) {
          this.circleRadius = meters;
        }
      }
    },
    resetDates() {
      this.dates = [];
      this.selectedDate = null;
    },
    scheduleFetchDates() {
      this.resetDates();
      clearTimeout(this.datesTimer);
      if (!this.geofencePayload) return;
      this.datesTimer = setTimeout(this.fetchDates, 400);
    },
    async fetchDates() {
      if (!this.geofencePayload) return;
      this.datesLoading = true;
      try {
        const env = await CropProgressionService.getAvailableDates(this.geofencePayload);
        this.dates = (env && env.data && env.data.dates) || [];
      } catch (e) {
        this.dates = [];
        this.$notify({
          text: this.$t('reports.cropProgression.datesFailed'),
          type: 'error',
        });
      } finally {
        this.datesLoading = false;
      }
    },
    async onGeojsonUpload(file) {
      if (!file) {
        this.geojsonCoords = [];
        this.geojsonS3Path = null;
        return;
      }
      // Parse geometry for the map + payload
      const text = await file.text().catch(() => null);
      if (!text) return;
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        this.$notify({
          title: this.$t('reports.cropProgression.invalidGeojsonFile'),
          text: this.$t('reports.cropProgression.invalidGeojsonFileDescription'),
          type: 'error',
        });
        return;
      }
      const coords = this.extractPolygonFromGeojson(parsed);
      if (coords.length < 3) {
        this.$notify({
          title: this.$t('reports.cropProgression.invalidGeojsonFile'),
          text: this.$t('reports.cropProgression.invalidGeojsonFileDescription'),
          type: 'error',
        });
        return;
      }
      this.geojsonCoords = coords;
      // Upload the raw file to S3 via pre-signed URL and store the object key
      this.uploadingGeojson = true;
      try {
        const { key } = await S3UploadService.singleUpload(file, 'crop-progression/geojson');
        this.geojsonS3Path = key;
      } catch (e) {
        this.$notify({
          text: this.$t('reports.cropProgression.uploadFailed'),
          type: 'error',
        });
      } finally {
        this.uploadingGeojson = false;
      }
    },
    extractPolygonFromGeojson(geojson) {
      const ringToLatLng = (ring) => ring.map((c) => ({ lat: c[1], lng: c[0] }));
      try {
        if (geojson.type === 'FeatureCollection' && geojson.features && geojson.features.length) {
          return this.extractPolygonFromGeojson(geojson.features[0]);
        }
        const geom = geojson.type === 'Feature' ? geojson.geometry : geojson;
        if (!geom) return [];
        if (geom.type === 'Polygon') return ringToLatLng(geom.coordinates[0]);
        if (geom.type === 'MultiPolygon') return ringToLatLng(geom.coordinates[0][0]);
      } catch (e) {
        return [];
      }
      return [];
    },
    async generate() {
      if (!this.canGenerate) return;
      this.generating = true;
      try {
        const payload = { ...this.geofencePayload, selectedDate: this.selectedDate };
        const env = await CropProgressionService.generateReport(payload);
        const report = env && env.data && env.data.report;
        if (report && report.reportUid) {
          this.$router.push({
            name: 'cropProgressionView',
            params: { reportUid: report.reportUid },
          });
        } else {
          this.$notify({
            text: this.reportErrorMessage({ data: env }, 'reports.cropProgression.generateFailed'),
            type: 'error',
          });
        }
      } catch (e) {
        this.$notify({
          text: this.reportErrorMessage(e, 'reports.cropProgression.generateFailed'),
          type: 'error',
        });
      } finally {
        this.generating = false;
      }
    },
    cancel() {
      this.$router.push({ name: 'cropProgressionList' });
    },
  },
};
</script>

<style scoped>
.header-actions .v-btn {
  min-width: 145px;
  height: 40px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: none;
}
.compute-card {
  background: #f4fbf7;
}
</style>
